import * as React from 'react';
import {Link} from "react-router-dom";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import {
    ICustomerPortalService,
    Policy,
    Types,
    ValuableItemCoverLocation,
    ValuableItemTypeEnum,
    InsuredValuableItem,
    resourcesHelper,
    HomeCoverTier,
    CLAIM_TYPE_OTHER,
    POLICY_STATUS_MTC,
    PREMIUM_TYPE_ANNUAL,
    InstalmentsInfo,
    INSTALMENT_STATUS_PENDING_PAYMENT,
    CoverChange,
    OptionalExtras
} from "@falcon/services";
import {
    formatDate,
    getAddress,
    getAmountToDisplay,
    getCoverLevelWording,
    getHomeCoverTierWording,
    getLink,
    setSessionStorage,
} from "../../../../../CommonFunctions";
import {lazyInject} from "@athena-portal/core";
import {Overlay} from "@athena-portal/components";
import {
    convertDateTime,
    renderCoverStartDate,
    Detail,
    DetailKey,
    DetailValue,
    SectionContents,
    Section,
    AnnualHomeDocumentListModal,
    getHomeSelectedTierIconName,
    SectionHeading,
    isCoverLevelContentsOnly,
    isCoverLevelBuildingsOnly, isPropertyOwned, latestCoverChangeDate
} from "@falcon/plugin-falcon-journey";
import {ILookupService, Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey";
import {ListItem} from "@athena-portal/journey-model";
import * as moment from "moment";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";

export interface IProps {
    policy: Policy;
    currentlyActivePolicy?: Policy;
    editAddress?: boolean;
    isOldPolicy: boolean;
    hasDebt: boolean;
}

export interface IState {
    maritalStatus?: string
    employmentStatus?: string
    jpEmploymentStatus?: string
    occupation?: string
    jpOccupation?: string
    occupationIndustry?: string
    jpOccupationIndustry?: string
    secondaryOccupation?: string
    jpSecondaryOccupation?: string
    secondaryOccupationIndustry?: string
    jpSecondaryOccupationIndustry?: string
    claimType?: string
    documentVersion?: string;
    instalmentsInfo?: InstalmentsInfo,
    isPolicyLapsedOrMTC: boolean,
    addedHEDate?: Date,
    addedHLEDate?: Date,
    dataLoaded: boolean
}

export class HomePolicySummary extends React.Component<IProps, IState> {
    protected pluginTemplates = new PluginTemplates();
    private readonly policyToRender: Policy;
    private readonly alertMessage: string = "Due to missed payment this action is not allowed";

    @lazyInject(Types.CustomerPortalService)
    protected customerPortalService: ICustomerPortalService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    protected lookupService: ILookupService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            maritalStatus: "",
            employmentStatus: "",
            jpEmploymentStatus: "",
            occupation: "",
            jpOccupation: "",
            occupationIndustry: "",
            jpOccupationIndustry: "",
            secondaryOccupation: "",
            jpSecondaryOccupation: "",
            secondaryOccupationIndustry: "",
            jpSecondaryOccupationIndustry: "",
            claimType: "",
            isPolicyLapsedOrMTC: this.isPolicyLapsedOrMTC(),
            dataLoaded: false
        }

        this.policyToRender = this.props.currentlyActivePolicy || this.props.policy;
    }

    componentDidMount() {
        this.pluginTemplates.setPageTitle("Policy summary");
        this.fetchLookupValues(this.policyToRender);

        const agreementVersionIds: number[] = this.props.policy.policyHistoryList
            .filter(ph => ph.status !== POLICY_STATUS_MTC)
            .map(ph => ph.id);

        this.customerPortalService
            .getCombinedInstalmentsInfo(agreementVersionIds)
            .subscribe((instalmentsInfo: any) => {
                this.setState({instalmentsInfo});
            });

        if (resourcesHelper.THEME !== "Britannia") {
            this.customerPortalService
                .getOptionalExtrasInformation(this.policyToRender.policyNumber)
                .subscribe(
                    (optionalExtraChanges: CoverChange[]) => {
                        this.setState({
                            addedHEDate: latestCoverChangeDate(optionalExtraChanges, OptionalExtras.HOME_EMERGENCY),
                            addedHLEDate: latestCoverChangeDate(optionalExtraChanges, OptionalExtras.HOME_LEGAL_EXPENSES),
                            dataLoaded: true
                        });
                    }
                );
        } else {
            this.setState({dataLoaded: true});
        }
    }

    public render(): React.ReactNode {
        if (!this.policyToRender?.interaction?.annualHomeFixedAddons?.homeEmergency) {
            setSessionStorage("claim", CLAIM_TYPE_OTHER);
        }

        if (!this.policyToRender || !this.state.dataLoaded) {
            return <Overlay show={true}/>
        } else {
            const coverLevel = this.policyToRender.interaction.coverLevel;
            const isContentsOnlyCover: boolean = isCoverLevelContentsOnly(coverLevel);
            const isBuildingsOnlyCover: boolean = isCoverLevelBuildingsOnly(coverLevel);

            return (
                <>
                    <img className={`home-policy-image ${this.props.isOldPolicy && "mt-4"}`}
                         src={`/${resourcesHelper.THEME}/img/policy-summary.png`} alt="Policy summary icon"/>
                    <Section>
                        <SectionHeading>
                            Policy details
                        </SectionHeading>
                        <SectionContents className="cover-details">
                            {this.renderCoverDetails()}
                            {!isBuildingsOnlyCover && this.renderSpecifiedItems()}
                            {!isBuildingsOnlyCover && this.renderPersonalBelongings()}
                            {!isBuildingsOnlyCover && this.renderBicycles()}
                            {this.renderFixedAddons(isContentsOnlyCover, isBuildingsOnlyCover)}
                            {this.renderExcesses()}
                        </SectionContents>
                    </Section>
                    {this.renderPolicyHolderDetails()}
                    {this.policyToRender.interaction.jointPolicyHolder && this.renderJointPolicyHolderDetails(this.policyToRender.interaction.jointPolicyHolder)}
                    {this.renderDocumentsDetails()}

                </>
            );
        }
    }

    protected renderCoverDetails(): React.ReactNode {
        const premiumType: string = this.policyToRender?.interaction.factors.premiumType;
        const price: number = this.getPrice(premiumType);
        const totalPaid: number = this.getTotalPaid();
        const tierIconFileName: string = getHomeSelectedTierIconName(this.policyToRender?.interaction.coverTier);
        return (
            <React.Fragment>

                <div className="policy-summary-tier">
                    <img className="tier-icon"
                         src={`/${resourcesHelper.THEME}/img/${tierIconFileName}.svg`}
                         alt="Tier logo"
                    /></div>
                <Detail>
                    <DetailValue>
                        <div className="detail-value price">
                            <span className="price-label">Price</span>
                            <div>
                                <span className="amount">{getAmountToDisplay(price)}</span>
                                <span
                                    className="year"> / {`${premiumType === PREMIUM_TYPE_ANNUAL ? "year*" : "month*"}`}</span>
                            </div>
                        </div>
                        {premiumType !== PREMIUM_TYPE_ANNUAL &&
                            <div className="detail-key price">
                                You pay <span className="font-weight-bold">{getAmountToDisplay(totalPaid)}</span> in
                                total
                            </div>
                        }
                    </DetailValue>
                </Detail>
                <Detail className="cover-start-date">
                    <DetailKey>
                        Cover start date:
                    </DetailKey>
                    <DetailValue>
                        {renderCoverStartDate(this.policyToRender.startDate)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Cover expiry date:
                    </DetailKey>
                    <DetailValue>
                        {convertDateTime(this.policyToRender.endDate)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Cover type:
                    </DetailKey>
                    <DetailValue>
                        {getCoverLevelWording(this.props.policy?.interaction.coverLevel)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Cover level:
                    </DetailKey>
                    <DetailValue>
                        {getHomeCoverTierWording(this.props.policy?.interaction.coverTier)}
                    </DetailValue>
                </Detail>
                <Detail className="address">
                    <DetailKey>
                        Insured address:
                    </DetailKey>
                    <DetailValue>
                        {getAddress(this.policyToRender?.insuredRealEstates[0].address)} {this.policyToRender?.insuredRealEstates[0].address.postalCode}
                    </DetailValue>
                    {this.props.editAddress && this.policyToRender?.status !== POLICY_STATUS_MTC &&
                        <div className="text-center">
                            <div className="detail-key">
                                <Link
                                    to={getLink('/journey/show/product/AnnualHomeInsurance/process/mtaChangeAddress/preset/' + this.props.policy?.interaction._id, false)}
                                    className={`text-decoration-underline cursor-pointer text-blue-1 muli-bold-16`}
                                    id="editAddress"
                                >
                                    Edit Address
                                </Link>
                            </div>
                        </div>}
                </Detail>
            </React.Fragment>
        );
    }

    protected renderSpecifiedItems(): React.ReactNode {
        let specifiedItems: InsuredValuableItem[] | undefined;
        if (this.props.currentlyActivePolicy) {
           specifiedItems = this.props.currentlyActivePolicy?.insuredValuableItems
                .filter(bicycle => bicycle.valuableItemType.code !== ValuableItemTypeEnum.BIKE);
        } else {
            specifiedItems = this.props.policy?.insuredValuableItems
                .filter(bicycle => bicycle.valuableItemType.code !== ValuableItemTypeEnum.BIKE);
        }

        const renderedSpecifiedItems: any = [];

        if (Array.isArray(specifiedItems) && specifiedItems.length !== 0) {
            specifiedItems.forEach(item => {
                renderedSpecifiedItems.push(
                    <p key={item.description}><b>{item.description}:</b> covered {item.coverLocation === ValuableItemCoverLocation.ANYWHERE ? "in and out of your home" : "in your home"} for £{item.value}<br/></p>
                    );
                }
            )
        } else {
            renderedSpecifiedItems.push(<p>None</p>);
        }
        return (
            <>
                <p className="fs-24 font-weight-bold mt-4">
                    Specified items:
                </p>
                {renderedSpecifiedItems}
            </>
        )
    }

    protected renderPersonalBelongings(): React.ReactNode {

        return (
            <Detail>
                <DetailKey>
                    Personal possession cover:
                </DetailKey>
                <DetailValue>
                    {this.policyToRender?.interaction.annualHomeExtraCovers.personalPossessionsCover && this.policyToRender?.interaction.annualHomeExtraCovers.personalPossessionCoverLimit ?
                        "Up to " + getAmountToDisplay(this.policyToRender?.interaction.annualHomeExtraCovers.personalPossessionCoverLimit) : "No cover"}
                </DetailValue>
            </Detail>
        )
    }

    protected renderBicycles(): React.ReactNode {
        const bicycles: InsuredValuableItem[] | undefined = this.policyToRender?.insuredValuableItems
            .filter(bicycle => bicycle.valuableItemType.code === ValuableItemTypeEnum.BIKE);

        const renderedBicycles: any = [];

        if (Array.isArray(bicycles) && bicycles.length !== 0) {
            bicycles.forEach(item => {
                    renderedBicycles.push(
                        <p key={item.description}><b>{item.description}:</b> covered {item.coverLocation === ValuableItemCoverLocation.ANYWHERE ? "in and out of your home" : "in your home"} for £{item.value}<br/></p>
                    );
                }
            )
        } else {
            renderedBicycles.push(<p>None</p>);
        }
        return (
            <>
                <p className="fs-24 font-weight-bold margin-top-32">
                    Bicycles:
                </p>
                {renderedBicycles}
            </>
        )
    }

    protected renderFixedAddons(isContentsOnlyCover: boolean, isBuildingsOnlyCover: boolean): React.ReactNode {
        const isOwned: boolean = isPropertyOwned(this.policyToRender?.interaction.realEstate[0].ownershipType);
        return (
            <React.Fragment>
                {!isContentsOnlyCover &&
                    <Detail>
                        <DetailKey>
                            Enhanced buildings accidental damage:
                        </DetailKey>
                        <DetailValue>
                            {this.getCoverStatus(this.policyToRender?.interaction.annualHomeFixedAddons.buildingAccidentalDamage)}
                        </DetailValue>
                    </Detail>
                }
                {!isBuildingsOnlyCover &&
                    <Detail>
                        <DetailKey>
                            Enhanced contents accidental damage:
                        </DetailKey>
                        <DetailValue>
                            {this.getCoverStatus(this.policyToRender?.interaction.annualHomeFixedAddons.contentsAccidentalDamage)}
                        </DetailValue>
                    </Detail>
                }
                <Detail>
                    <DetailKey>
                        Home Legal Expenses:
                    </DetailKey>
                    <DetailValue>
                        {this.getCoverStatus(this.policyToRender?.interaction.annualHomeFixedAddons.homeLegalExpenses)}
                    </DetailValue>
                </Detail>
                {(!isContentsOnlyCover || isOwned) &&
                    <Detail>
                        <DetailKey>
                            Home Emergency:
                        </DetailKey>
                        <DetailValue>
                            {this.getCoverStatus(this.policyToRender?.interaction.annualHomeFixedAddons.homeEmergency)}
                        </DetailValue>
                    </Detail>}
            </React.Fragment>
        )
    }

    protected renderExcesses(): React.ReactNode {
        const subsidenceLandslipOrHeave: number = this.policyToRender?.interaction.annualHomeExcesses.subsidence;
        const escapeOfWater: number = this.policyToRender?.interaction.annualHomeExcesses.escapeOfWater;
        const flood: number = this.policyToRender?.interaction.annualHomeExcesses.flood;
        const allOtherClaims: number = this.policyToRender?.interaction.annualHomeExcesses.allOtherClaims;
        const premiumType: string = this.props.policy?.interaction.factors.premiumType;
        const isContentsOnlyCover: boolean = isCoverLevelContentsOnly(this.props.policy?.interaction.coverLevel);

        return (
            <React.Fragment>
                <h2 className={`section-heading margin-top-32 ${this.props.isOldPolicy ? "mb-3" : "mb-0"}`}>Excesses</h2>

                {!isContentsOnlyCover &&
                    <Detail>
                        <DetailKey>
                            Subsidence, landslip or heave:
                        </DetailKey>
                        <DetailValue>
                            {getAmountToDisplay(subsidenceLandslipOrHeave)}
                        </DetailValue>
                    </Detail>
                }
                <Detail>
                    <DetailKey>
                        Escape of water:
                    </DetailKey>
                    <DetailValue>
                        {getAmountToDisplay(escapeOfWater)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        Flood:
                    </DetailKey>
                    <DetailValue>
                        {getAmountToDisplay(flood)}
                    </DetailValue>
                </Detail>
                <Detail>
                    <DetailKey>
                        All other claims:
                    </DetailKey>
                    <DetailValue>
                        {getAmountToDisplay(allOtherClaims)}
                    </DetailValue>
                </Detail>
                <Detail className={`premium-tax ${this.props.isOldPolicy && "mb-4"}`}>
                    {!this.state.isPolicyLapsedOrMTC &&
                        <DetailValue>
                            <ButtonLink
                                className="mb-0"
                                primaryNoArrow={true}
                                to={`/journey/show/product/AnnualHomeInsurance/process/mta/presetPage/${this.props.policy?.interaction._id}`}
                                disabled={this.props.hasDebt}
                                alertMessage={this.alertMessage}
                            >
                                Edit policy details
                            </ButtonLink>
                        </DetailValue>}
                    <DetailValue>
                        {premiumType === PREMIUM_TYPE_ANNUAL ?
                            '* includes any changes made during the policy year' :
                            '* includes any changes that you’ve made'
                        }
                    </DetailValue>
                    <DetailValue>
                        {premiumType === PREMIUM_TYPE_ANNUAL ?
                            'incl. Insurance Premium Tax' :
                            'incl. Interest and Insurance Premium Tax'
                        }
                    </DetailValue>
                </Detail>
            </React.Fragment>
        );
    }

    protected renderPolicyHolderDetails() {
        return (
            <React.Fragment>
                <img className="home-account-details-image mb-2 margin-bottom-0"
                     src={`/${resourcesHelper.THEME}/img/policyholder.svg`}
                     alt="Account details icon"/>
                <Section>
                    <SectionHeading>
                        Policyholder details
                    </SectionHeading>
                    <SectionContents>
                        <Detail>
                            <DetailKey className="policyholder-name">
                                {this.policyToRender?.policyHolder.firstName} {this.policyToRender?.policyHolder.lastName}
                            </DetailKey>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Date of birth:
                            </DetailKey>
                            <DetailValue>
                                {moment(this.policyToRender?.policyHolder.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Email:
                            </DetailKey>
                            <DetailValue>
                                {this.policyToRender?.policyHolder.email}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Phone number:
                            </DetailKey>
                            <DetailValue>
                                {this.policyToRender?.policyHolder.phoneMobile}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Marital status:
                            </DetailKey>
                            <DetailValue>
                                {this.state.maritalStatus}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Employment status:
                            </DetailKey>
                            <DetailValue>
                                {this.state.employmentStatus}
                            </DetailValue>
                        </Detail>
                        {this.state.occupation!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Occupation:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.occupation}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.occupationIndustry!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Industry:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.occupationIndustry}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.secondaryOccupation!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Secondary occupation:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.secondaryOccupation}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.secondaryOccupationIndustry!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Secondary occupation industry:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.secondaryOccupationIndustry}
                                </DetailValue>
                            </Detail>
                        }
                        <Detail className="margin-bottom-32">
                            <DetailKey>
                                Claims in the last 5 years:
                            </DetailKey>
                            <DetailValue>
                                {this.renderClaimOrNone()}
                            </DetailValue>
                        </Detail>
                        {!this.state.isPolicyLapsedOrMTC &&
                            <Detail>
                                <DetailValue>
                                    <ButtonLink
                                        className="mb-0"
                                        primaryNoArrow={true}
                                        to={`/journey/show/product/AnnualHomeInsurance/process/mta/presetPage/${this.props.policy?.interaction._id}`}
                                        disabled={this.props.hasDebt}
                                        alertMessage={this.alertMessage}
                                    >
                                        Edit policyholder details
                                    </ButtonLink>
                                </DetailValue>
                            </Detail>}
                    </SectionContents>
                </Section>
            </React.Fragment>);
    }

    protected renderJointPolicyHolderDetails(jointPolicyHolder: any) {
        return (
            <React.Fragment>
                <img className="home-account-details-image mb-2 home-icon"
                     src={`/${resourcesHelper.THEME}/img/account-details.png`}
                     alt="Account details icon"/>
                <Section className={`${this.props.isOldPolicy && "mb-4"}`}>
                    <SectionHeading>
                        Joint policyholder
                    </SectionHeading>
                    <SectionContents>
                        <Detail>
                            <DetailKey>
                                {jointPolicyHolder.firstName} {jointPolicyHolder.lastName}
                            </DetailKey>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Date of birth:
                            </DetailKey>
                            <DetailValue>
                                {moment(jointPolicyHolder.dateOfBirth, moment.ISO_8601).format("D MMMM YYYY")}
                            </DetailValue>
                        </Detail>
                        <Detail>
                            <DetailKey>
                                Employment status:
                            </DetailKey>
                            <DetailValue>
                                {this.state.jpEmploymentStatus}
                            </DetailValue>
                        </Detail>
                        {this.state.jpOccupation!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Occupation:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.jpOccupation}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.jpOccupationIndustry!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Industry:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.jpOccupationIndustry}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.jpSecondaryOccupation!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Secondary occupation:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.jpSecondaryOccupation}
                                </DetailValue>
                            </Detail>
                        }
                        {this.state.jpSecondaryOccupationIndustry!.length > 1 &&
                            <Detail>
                                <DetailKey>
                                    Secondary occupation industry:
                                </DetailKey>
                                <DetailValue>
                                    {this.state.jpSecondaryOccupationIndustry}
                                </DetailValue>
                            </Detail>
                        }
                    </SectionContents>
                </Section>
            </React.Fragment>);
    }

    protected renderDocumentsDetails() {
        return (
            <React.Fragment>

                <img className="home-documents-image mb-2 home-icon"
                     src={`/${resourcesHelper.THEME}/img/policy_documents.svg`}
                     alt="Policy documents icon"/>
                <Section>
                    <SectionContents>

                        <AnnualHomeDocumentListModal
                            coverTier={this.props.policy.interaction.coverTier}
                            policyDate={new Date(this.props.policy.startDate)}
                            withTitle={true}
                            title={"Policy documents"}
                            annualHomeFixedAddons={this.policyToRender?.interaction?.annualHomeFixedAddons}
                            addedHEDate={this.state.addedHEDate}
                            addedHLEDate={this.state.addedHLEDate}
                        />
                    </SectionContents>
                </Section>
            </React.Fragment>);
    }


    protected renderClaimOrNone(): React.ReactNode {
        return (<>
            {this.policyToRender?.interaction.policyHolder.hasClaims && this.policyToRender?.interaction.policyHolder.numberOfClaims > 0 && this.policyToRender?.interaction.policyHolder.claims?.length > 0 ?
                this.renderClaims(this.policyToRender?.interaction.policyHolder.claims) : 'None'}
        </>);
    }

    protected renderClaims(claims: any): React.ReactNode | React.ReactNode[] {
        return claims.map((claim: any, index: any) =>
            <div key={claim.claimType + index}
                 className="detail-value">{this.resolveClaimType(claim.claimType)} in {" "}
                {formatDate(claim.date, "MMM YYYY")}{index !== claims.length - 1 && ", "}</div>
        )
    }

    protected resolveClaimType(claimId: any): string | undefined {
        let claimType: string | undefined;
        if (sessionStorage.classifier_homeClaimType) {
            const claimTypeNamingList: any = JSON.parse(sessionStorage.classifier_homeClaimType);
            claimType = claimTypeNamingList.find((item: any) => item.id.toString() === claimId.toString())?.value;
            if (!claimType) {
                claimType = this.fetchClaimType(claimId);
            }
        } else {
            claimType = this.fetchClaimType(claimId);
        }

        return claimType;
    }

    private isPolicyLapsedOrMTC(): boolean {
        return moment.utc(this.props.policy.endDate, moment.ISO_8601).isBefore(moment())
            || this.props.currentlyActivePolicy?.status === POLICY_STATUS_MTC || this.props.policy?.status === POLICY_STATUS_MTC;
    }

    private fetchClaimType(claimId: any): string | undefined {
        let claimType: string | undefined;
        this.lookupService.fetchLookupValues("homeClaimType", "en-UK").subscribe((lookupValues: ListItem[]) => {
            claimType = lookupValues.find(item => item.id === claimId.toString())?.value;
        });

        return claimType;
    }

    protected getCoverStatus(cover: boolean): string {
        if (this.policyToRender?.interaction.coverTier === HomeCoverTier.GOLD) {
            return "Included";
        }
        if (cover) {
            return "Included";
        } else {
            return "Not included";
        }
    }

    protected fetchLookupValues = (p: Policy) => {
        const languageCode = "en-UK";

        const valuesToPopulate = [
            {
                classifier: "maritalStatus",
                field: p.interaction.policyHolder.maritalStatus,
                stateName: "maritalStatus"
            },
            {
                classifier: "employmentStatus",
                field: p.interaction.policyHolder.employmentStatus,
                stateName: "employmentStatus"
            },
            {
                classifier: "employmentStatus",
                field: p.interaction.jointPolicyHolder?.employmentStatus,
                stateName: "jpEmploymentStatus"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.policyHolder.occupation,
                stateName: "occupation"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.jointPolicyHolder?.occupation,
                stateName: "jpOccupation"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.policyHolder.occupationIndustry,
                stateName: "occupationIndustry"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.jointPolicyHolder?.occupationIndustry,
                stateName: "jpOccupationIndustry"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.policyHolder.secondaryOccupation,
                stateName: "secondaryOccupation"
            },
            {
                classifier: "wolfOccupation",
                field: p.interaction.jointPolicyHolder?.secondaryOccupation,
                stateName: "jpSecondaryOccupation"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.policyHolder.secondaryOccupationIndustry,
                stateName: "secondaryOccupationIndustry"
            },
            {
                classifier: "wolfOccupationIndustry",
                field: p.interaction.jointPolicyHolder?.secondaryOccupationIndustry,
                stateName: "jpSecondaryOccupationIndustry"
            },
            {
                classifier: "homeClaimType",
                field: null,
                stateName: ""
            }
        ];

        valuesToPopulate.forEach(item => {
            const lookupFieldValue = item.field ?? undefined;
            this.lookupService.fetchLookupValues(item.classifier, languageCode, lookupFieldValue).subscribe((lookupValues: ListItem[]) => {
                const fetchedLookupItem = lookupValues.find(lookupValue => lookupValue.id === item.field);
                if (fetchedLookupItem) {
                    this.setState((prevState) =>
                        ({...prevState, [item.stateName]: fetchedLookupItem.value}));
                }
            });
        });

    }

    private getPrice(premiumType: string) {
        const price = premiumType === PREMIUM_TYPE_ANNUAL
            ? this.policyToRender?.interaction.factors.annualPremium
            : this.policyToRender?.interaction.paymentDetails.instalmentPlan.monthlyPayment;

        return price === 0 && this.state.instalmentsInfo ? this.getInstallmentPayment() : price;
    }

    private getInstallmentPayment(): number {
        return this.state.instalmentsInfo?.instalments
            .find(instalment => instalment.status === INSTALMENT_STATUS_PENDING_PAYMENT)?.amount ?? 0;
    }

    private getTotalPaid(): number {
        return this.policyToRender?.interaction.paymentDetails.instalmentPlan?.totalAmount
            || this.state.instalmentsInfo?.totalAmount
    }
}
